'use client';

import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { NavigationDropDownProps } from './interfaces';

export const NavigationDropDown = ({ label, isActive, panelClassName, children }: NavigationDropDownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => document.removeEventListener('click', handleClickOutside);
    }, [isOpen]);

    const close = () => setIsOpen(false);

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                className={`
                    px-3 py-3 rounded-[8px] cursor-pointer flex items-center
                    transition-colors text-white duration-300
                    ${isActive ? 'bg-white/30' : 'bg-[transparent] hover:text-gray-400'}
                `}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="pr-[10px]">{label}</span>
                <ChevronDown className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} size={24} />
            </button>

            <ul
                className={cn(
                    'px-[10px] pt-1 pb-[0.5px] bg-[rgba(14,14,14,1)] rounded-[8px] min-w-[225px]',
                    'static max-lg:mt-2 lg:absolute lg:z-1',
                    panelClassName,
                    isOpen ? 'block' : 'hidden',
                )}
            >
                {children(close)}
            </ul>
        </div>
    );
};
